import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000

export interface DismissedEntry {
  id: string
  dismissedAt: number
}

export function alertTypeFromDismissId(id: string): string {
  const parts = id.split('-')
  return parts[1] ?? ''
}

export const useDismissibleAlertsStore = defineStore(
  'dismissibleAlerts',
  () => {
    const entries = ref<DismissedEntry[]>([])
    const defaultTimeToLiveMs = ref<number | null>(ONE_WEEK_MS)
    const timeToLiveMsByAlertType = ref<Record<string, number | null>>({})

    function getTimeToLiveMs(id: string): number | null {
      const type = alertTypeFromDismissId(id)
      const byType = timeToLiveMsByAlertType.value[type]
      if (byType !== undefined) return byType
      return defaultTimeToLiveMs.value
    }

    function isEntryActive(
      entry: DismissedEntry,
      now: number = Date.now()
    ): boolean {
      const ttl = getTimeToLiveMs(entry.id)
      if (ttl === null) return true
      return entry.dismissedAt + ttl > now
    }

    const dismissedIds = computed(() => {
      const now = Date.now()
      const set = new Set<string>()
      for (const entry of entries.value) {
        if (isEntryActive(entry, now)) {
          set.add(entry.id)
        }
      }
      return set
    })

    function isDismissed(id: string): boolean {
      return dismissedIds.value.has(id)
    }

    function dismiss(id: string): void {
      const now = Date.now()
      const filtered = entries.value.filter((e) => e.id !== id)
      filtered.push({ id, dismissedAt: now })
      const pruned = filtered.filter((e) => isEntryActive(e, now))
      entries.value = pruned
    }

    return {
      entries,
      defaultTimeToLiveMs,
      timeToLiveMsByAlertType,
      dismissedIds,
      isDismissed,
      dismiss,
      getTimeToLiveMs,
      isEntryActive
    }
  },
  {
    persist: true
  }
)
